import { DocumentService } from '@dealstack/frontend/shared/dataExchange/services/document/document.service';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { fileTypeFromBlob } from 'file-type';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';

interface Options
	extends Omit<
		UseQueryOptions<
			{ blob: Blob; fileExtension: string },
			unknown,
			{ blob: Blob; fileExtension: string },
			[string, { fileId: string; dealId?: string; organizationId?: string }]
		>,
		'queryKey' | 'queryFn' | 'enabled' | 'onError' | 'onSuccess'
	> {
	dealId?: string;
	organizationId?: string;
	fileId: string;
}

export const useDownloadedFileBlob = ({ fileId, dealId, organizationId, ...options }: Options) => {
	const tCapManage = useTranslationCapManage();
	return useQuery({
		queryFn: async () => {
			try {
				const data = await DocumentService.getDownloadPreSignedUrl({
					dealId,
					organizationId,
					fileId,
				});
				const downloadUrl = data.payload?.downloadUrl;
				if (!downloadUrl) {
					throw new Error('Failed to get download URL');
				}
				const response = await fetch(downloadUrl);
				if (!response.ok) {
					throw new Error(`Failed to download file: ${response.statusText}`);
				}
				const blob = await response.blob();
				const fileType = await fileTypeFromBlob(blob);

				return {
					blob,
					fileExtension: fileType?.ext,
				};
			} catch (error) {
				showToastV2({
					title: tCapManage('documents.errors.download-document'),
					state: 'error',
				});
				throw error;
			}
		},
		queryKey: [API_PATHS.FILE_ID_GET, { fileId, dealId, organizationId }],
		enabled: !!fileId,
		...options,
	});
};
